import { useCallback, useEffect, useState } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { SignaturesIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import {
	useMobileNativeShare,
	useShareCtaPlacementEnabled,
	useSignatureTracking,
} from '#src/app/pages/petitionDetails/shared/hooks';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import { ActionButtonWrapper } from './ActionButtonWrapper';
import type { ActionPanelVariant } from './MobileActionButton';
import { MobileSignModal, usePreloadMobileSignModal } from './MobileSignModal';
import { useMobileModalContext } from './shared/useMobileModalContext';
import { useShowSignForm } from './shared/useShowSignForm';
import { SignCtaShareAffordance } from './SignCtaShareAffordance';

type Props = {
	/**
	 * Whether this render site paints the share affordance. Distinct from `ownsShareExposure`: under
	 * `inline-then-sticky` both sites paint it and only the in-flow one reports exposure.
	 */
	rendersShareAffordance?: boolean;
	/**
	 * Renders the share affordance without the sign button. For
	 * the `sticky-sign-share-inline` layout (CHANGE-97346 variant_2), where the sign CTA moves into
	 * the docked bar and only the share CTA stays in flow.
	 *
	 * Deliberately a flag on this component rather than a separate share-only component: the sign
	 * modal and its `showSignForm` auto-open both live here and are gated on `!sticky`, so keeping the
	 * in-flow instance mounted means deep-link auto-open keeps working with no extra wiring.
	 */
	hideSignButton?: boolean;
	/**
	 * Whether this render site is the one that reports share-cta-copy `$exposure` for the current
	 * layout. Passed down rather than derived from `sticky`, because which site owns it varies by
	 * layout — see `ownsShareExposure` in MobileActionButton.
	 */
	ownsShareExposure: boolean;
	sticky: boolean;
	visible: boolean;
	variant: ActionPanelVariant;
};

/**
 * `shareCtaPlacementEnabled` already folds in `rendersShareAffordance` (see below), so it's not
 * re-checked here.
 *
 * `ownsShareExposure` replaces what used to be a bare `!sticky` check. Both render sites mount
 * this component, so exactly one of them has to fire share-cta-copy's `$exposure` — but which one
 * depends on the layout, and `!sticky` only happened to be right for `inline-then-sticky`. Under
 * `sticky-from-load` it fired from neither, while the docked bar rendered the treated affordance.
 *
 * `visible` is checked on top, because `ActionButtonWrapper` renders `null` for an in-flow instance
 * with `visible: false` — the affordance is then never on screen, so exposure would enrol a viewer
 * who cannot convert on it.
 */
function shouldTrackShareCtaCopyExperiment(
	ownsShareExposure: boolean,
	visible: boolean,
	hasShareAction: boolean,
	shareCtaPlacementEnabled: boolean,
) {
	return ownsShareExposure && visible && hasShareAction && shareCtaPlacementEnabled;
}

/**
 * Returns `null` for a share-only render site with no share action to offer, which is what lets the
 * caller skip the wrapper rather than leave an empty box in the layout. The other two branches
 * reproduce the pre-variant_2 markup exactly, including the flex wrapper when the affordance is
 * enabled but its share action hasn't resolved yet.
 */
function pickActionContent({
	hideSignButton,
	hasShareAction,
	shareAffordance,
	shareCtaPlacementEnabled,
	signButton,
}: {
	hideSignButton: boolean;
	hasShareAction: boolean;
	shareAffordance: React.ReactNode;
	shareCtaPlacementEnabled: boolean;
	signButton: React.ReactNode;
}): React.ReactNode {
	if (hideSignButton) {
		return hasShareAction ? shareAffordance : null;
	}

	if (shareCtaPlacementEnabled) {
		return (
			<div className="flex flex-col gap-2">
				{signButton}
				{shareAffordance}
			</div>
		);
	}

	return signButton;
}

export const MobileSignButtonContainer: React.FC<Props> = ({
	rendersShareAffordance = true,
	hideSignButton = false,
	ownsShareExposure,
	sticky,
	visible,
	variant,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const { trackMobileSignIntent } = useSignatureTracking();

	const context = useMobileModalContext();

	usePreloadMobileSignModal();

	const {
		data: {
			experiments: { petitionDetailsShareCtaCopyExperiment },
		},
	} = usePetitionDetailsPageUserData();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();

	// `sticky` only names the tracking context here; it does not decide whether this instance renders
	// the affordance. That is `ownsShareExposure`, which the layout derives — under
	// `sticky-from-load` only the docked instance owns it, under `sticky-sign-share-inline` only the
	// in-flow one does, and under `inline-then-sticky` (control) it is the in-flow one as before.
	const trackingContext = sticky ? 'petition_show_sign_cta_sticky' : 'petition_show_sign_cta_inline';

	// Evaluated in every arm, control included: this is the availability half of the render/exposure
	// predicate, and it must be arm-independent so the compared populations are identical.
	const shareAction = useMobileNativeShare(trackingContext);
	const shareCtaPlacementEnabled = useShareCtaPlacementEnabled() && rendersShareAffordance;
	const trackShareCtaCopyExperiment = shouldTrackShareCtaCopyExperiment(
		ownsShareExposure,
		visible,
		!!shareAction,
		shareCtaPlacementEnabled,
	);
	// resetKeys is required, not optional: this component instance persists across an SPA
	// petition-to-petition navigation (no remount), and variation/experimentName are otherwise
	// unchanged, so without the petition id the new petition's legitimate exposure would be deduped
	// against the previous one. Same requirement as useTreatSignFormAddressRemovalExperiment on this page.
	const trackShareCtaCopyExposure = useTrackExperimentExposure(petitionDetailsShareCtaCopyExperiment, {
		resetKeys: [petitionId],
	});

	// Fired from a stable effect on this component instance, not a conditionally-mounted
	// <TrackExperimentExposure>: `shareAction` resolves asynchronously (see useMobileNativeShare,
	// which queries on `sessionUuid`) and can flicker unavailable → available after mount. A
	// conditionally-mounted tracker would unmount/remount on that flicker, resetting its per-mount
	// dedup flag (useTrackOnce) and double-firing $exposure — this component instance persists across
	// the flicker, so calling the hook's returned function here keeps the dedup flag stable instead.
	// Both instances of MobileActionButtonContainer render this component; `ownsShareExposure` in
	// shouldTrackShareCtaCopyExperiment is what prevents the *other* double-fire vector, across the
	// two sibling mounts — exactly one of them owns the affordance in every layout, so exactly one
	// fires. It replaced a plain `!sticky` guard, which fired from the in-flow mount even in the
	// layouts where the docked mount is the one rendering the affordance.
	useEffect(() => {
		if (trackShareCtaCopyExperiment) {
			void trackShareCtaCopyExposure();
		}
	}, [trackShareCtaCopyExperiment, trackShareCtaCopyExposure]);

	const openModal = useCallback(() => {
		trackMobileSignIntent();
		setIsOpen(true);
	}, [trackMobileSignIntent]);

	const openModalWithoutTracking = useCallback(() => {
		setIsOpen(true);
	}, []);

	const signButton = (
		<Button
			variant="primary"
			onClick={openModal}
			leadingIcon={<SignaturesIcon size="small" />}
			data-qa="mobile-sign-button"
			data-testid="mobile-sign-button"
			data-gtm="ga4-sign-button"
			size="large"
			className="w-full max-w-none"
		>
			<Translate value="corgi.petition_gamma.petition_sign.action" />
		</Button>
	);

	const shareAffordance = shareCtaPlacementEnabled ? (
		<SignCtaShareAffordance shareAction={shareAction} variation={petitionDetailsShareCtaCopyExperiment.variation} />
	) : null;

	const actionContent = pickActionContent({
		hideSignButton,
		hasShareAction: !!shareAction,
		shareAffordance,
		shareCtaPlacementEnabled,
		signButton,
	});

	return (
		<>
			{/* we're looking at breakpoints to decide whether to open the modal, which cannot be done server-side */}
			{/* Only auto-open on the non-sticky instance to avoid opening modal twice (both sticky and non-sticky render this component) */}
			{!sticky && (
				<ClientRender>
					<MobileSignButtonAutoOpen onOpen={openModalWithoutTracking} />
				</ClientRender>
			)}
			{actionContent ? (
				/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */
				<ActionButtonWrapper visible={visible} sticky={sticky}>
					{actionContent}
				</ActionButtonWrapper>
			) : null}

			<MobileSignModal context={context} isOpen={isOpen} setIsOpen={setIsOpen} variant={variant} />
		</>
	);
};
function MobileSignButtonAutoOpen({ onOpen }: { onOpen: () => void }) {
	const showSignForm = useShowSignForm();

	useEffect(() => {
		if (showSignForm) {
			/**
			 * There are some updates that happen right before showing the modal (such as populating the form fields)
			 * that need to complete first so that the modal opens with the correct data.
			 * Using setTimeout to defer the onOpen call to the next event loop iteration and avoid a race condition.
			 */
			setTimeout(() => {
				onOpen();
			}, 0);
		}
		// disabling dependency check to avoid attempting to open the modal multiple times
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}
